/**
 * Edit copywriting modal
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, TreeSelect, message } from 'antd';
import { copywritingApi, folderApi } from '../../services/resourceService';
import { ResourceScope, ResourceFolder, FolderType, Copywriting } from '../../types/resource';

const { TextArea } = Input;
const { Option } = Select;

interface EditCopywritingModalProps {
  visible: boolean;
  copywriting: Copywriting | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditCopywritingModal: React.FC<EditCopywritingModalProps> = ({
  visible,
  copywriting,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<ResourceFolder[]>([]);

  // Load folders and set form values
  useEffect(() => {
    if (visible && copywriting) {
      loadFolders();
      form.setFieldsValue({
        title: copywriting.title,
        content: copywriting.content,
        scope: copywriting.scope,
        folder_id: copywriting.folder_id,
        tags: copywriting.tags ? copywriting.tags.join(', ') : '',
      });
    }
  }, [visible, copywriting]);

  const loadFolders = async () => {
    if (!copywriting) return;
    try {
      const result = await folderApi.getTree(FolderType.COPYWRITING, copywriting.scope);
      setFolders(result);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Convert folders to tree data
  const buildTreeData = (folders: ResourceFolder[]): any[] => {
    return folders.map((folder) => ({
      title: folder.name,
      value: folder.id,
      children: folder.children ? buildTreeData(folder.children) : [],
    }));
  };

  const handleSubmit = async () => {
    if (!copywriting) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      await copywritingApi.update(copywriting.id, {
        title: values.title,
        content: values.content,
        folder_id: values.folder_id || null,
        tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()) : [],
      });

      message.success('更新成功');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="编辑文案"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="title"
          label="标题"
          rules={[{ required: true, message: '请输入标题' }]}
        >
          <Input placeholder="请输入文案标题" />
        </Form.Item>

        <Form.Item
          name="content"
          label="内容"
          rules={[{ required: true, message: '请输入内容' }]}
        >
          <TextArea
            rows={6}
            placeholder="请输入文案内容"
            showCount
            maxLength={5000}
          />
        </Form.Item>

        <Form.Item
          name="scope"
          label="类型"
        >
          <Select disabled>
            <Option value={ResourceScope.ENTERPRISE}>企业文案</Option>
            <Option value={ResourceScope.PERSONAL}>个人文案</Option>
          </Select>
        </Form.Item>

        <Form.Item name="folder_id" label="分组">
          <TreeSelect
            placeholder="请选择分组（可选）"
            allowClear
            treeData={buildTreeData(folders)}
          />
        </Form.Item>

        <Form.Item name="tags" label="标签">
          <Input placeholder="多个标签用逗号分隔" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCopywritingModal;
